import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import PCContainer from "../../components/atoms/layout/pcContainer";

import useDisplay from "../../hooks/useDisplay";
import { route } from "../../router/route";
import LSRgDisplay02 from "../../components/molecules/text/linSeedKr/Rg/lsRgDisplay02";
import Empty from "../../components/molecules/empty";
import Pagination from "../../components/molecules/pagination";
import LSRgH2HeadLine from "../../components/molecules/text/linSeedKr/Rg/lsRgH2HeadLine";
import CommunityItem from "../../components/organisms/community/communityItem";
import MainBtn from "../../components/atoms/button/mainBtn";
import LSRgBody01 from "../../components/molecules/text/linSeedKr/Rg/lsRgBody01";
import CommunityApi from "../../api/community.api";
import { errorHandler } from "../../util/errorHandler";
import SelecteBoxValue from "../../components/organisms/selectbox/selecteBoxValue";
import LSRgCaption02 from "../../components/molecules/text/linSeedKr/Rg/lsRgCaption02";
import { toast } from "react-toastify";
import StyledCategoryItem from "../../components/molecules/button/styledCategoryItem";
import { macOSCheck } from "../../util/macOSCheck";
import Loading from "../../components/templates/loading";

const CommunityList = () => {
  const { isMobile, isTablet, isDesktop } = useDisplay();
  const isSmall = isMobile || isTablet;

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams(); // eslint-disable-line no-unused-vars

  const [categoryBoxOpen, setCategoryBoxOpen] = useState(false);
  const [categoryValue, setCategoryValue] = useState(-1);
  const [categoryList, setCategoryList] = useState([
    {
      title: "전체보기",
      value: -1,
    },
  ]);

  const [popularList, setPopularList] = useState([]);
  const [communityList, setCommunityList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPopularLoading, setIsPopularLoading] = useState(true);

  const page = useMemo(() => {
    const requestedPage = Number(searchParams.get("page"));
    return Number.isInteger(requestedPage) && requestedPage > 0
      ? requestedPage
      : 1;
  }, [searchParams]);

  const requestedCategoryValue = useMemo(() => {
    const value = Number(searchParams.get("categoryValue"));
    return Number.isInteger(value) && (value === -1 || value > 0) ? value : -1;
  }, [searchParams]);

  const [pagination, setPagination] = useState({
    size: 5, // 한 페이지에 표시될 게시글 수
    rangeSize: 5, // 하단 페이지 사이즈
    totalCount: 0,
    totalPages: 0,
    first: false,
    last: false,
  });

  useEffect(() => {
    categoryListFunc();
    hotDataListFunc();
  }, []);

  // ---------- 마운트가 되면 ---------- //
  useEffect(() => {
    setCategoryValue(requestedCategoryValue);

    dataListFunc(requestedCategoryValue);
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryListFunc = async () => {
    try {
      const columnCList = (
        await CommunityApi.getCategoryList({
          page: 1,
          size: 0,
          activation: "Active",
        })
      ).data.data.content;
      const categoryTemp = (columnCList ?? []).map((item) => {
        return {
          ...item,
          value: item.id,
        };
      });

      setCategoryList([{ title: "전체보기", value: -1 }, ...categoryTemp]);
    } catch (error) {
      errorHandler(error);
    }
  };

  const dataListFunc = async (selectedCategoryValue = -1) => {
    setIsLoading(true);
    try {
      const size = selectedCategoryValue > 0 ? 10 : 5;

      const data = {
        page: page,
        size: size,
      };

      if (selectedCategoryValue > 0) {
        data.communityCategoryId = selectedCategoryValue;
      }

      const response = (await CommunityApi.getList(data)).data.data;

      setCommunityList(response.content ?? []);
      setPagination((prev) => ({
        ...prev,
        size,
        totalCount: response.totalCount,
        totalPages: Math.ceil(response.totalCount / size),
      }));
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsLoading(false);
    }
  };

  const hotDataListFunc = async () => {
    setIsPopularLoading(true);
    try {
      const data = {
        page: 1,
        size: 5,
        sort: "viewCount",
        orderBy: "DESC",
      };

      const response = (await CommunityApi.getList(data)).data.data;

      setPopularList(response.content ?? []);
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsPopularLoading(false);
    }
  };

  return (
    <>
      <CommunityListSection>
        <PCContainer
          style={{
            flexDirection: "column",
            padding: isMobile
              ? "40px 20px 80px 20px"
              : isTablet
                ? "40px 60px 80px 60px"
                : "80px 0px 150px 0px",
            width: "100%",
          }}
        >
          <LSRgDisplay02
            style={{
              color: "#000000",
              marginBottom: isMobile ? 16 : isTablet ? 30 : 32,
              fontSize: isMobile ? 14 : 24,
              lineHeight: isMobile ? "19.6px" : "33.6px",
            }}
          >
            💭 커뮤니티
          </LSRgDisplay02>

          <div
            style={{
              display: "flex",
              flexDirection: isSmall ? "column" : "row",
              width: "100%",
              height: "fit-content",
              gap: 30,
            }}
          >
            {isSmall ? (
              <SelecteBoxValue
                style={{
                  marginBottom: 20,
                }}
                selectedValue={categoryValue}
                isOpen={categoryBoxOpen}
                wrapperHandler={() => {
                  setCategoryBoxOpen(!categoryBoxOpen);
                }}
                itemList={categoryList}
                selectedValueSetterFunc={(value) => {
                  setCategoryValue(value);
                  navigate(
                    `${route.community_list}?page=1&categoryValue=${value}`,
                  );
                }}
              />
            ) : (
              <TabContainer>
                {categoryList.map((tab) => (
                  <StyledCategoryItem
                    key={tab.value}
                    onClick={() => {
                      navigate(
                        `${route.community_list}?page=1&categoryValue=${tab.value}`,
                      );
                    }}
                    isActive={categoryValue === tab.value}
                    title={tab.title}
                  />
                ))}
              </TabContainer>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: isSmall ? "100%" : 995,
              }}
            >
              {categoryValue === -1 ? (
                <div
                  style={{
                    marginBottom: 60,
                  }}
                >
                  <LSRgDisplay02
                    style={{
                      color: "#000000",
                      marginBottom: isMobile ? 20 : 31,
                      fontSize: isMobile ? 16 : 24,
                      lineHeight: isMobile ? "22.4px" : "33.6px",
                    }}
                  >
                    🔥 지금 가장 인기있는 글이에요.
                  </LSRgDisplay02>
                  {isPopularLoading ? (
                    <Loading />
                  ) : popularList.length > 0 ? (
                    popularList.map((item, index) => {
                      return (
                        <CommunityItem
                          key={item.id}
                          data={item}
                          style={{
                            borderBottom:
                              popularList.length === index + 1
                                ? "1px solid #DBDDE0"
                                : "none",
                          }}
                        />
                      );
                    })
                  ) : (
                    <Empty title={"커뮤니티가 없습니다."} />
                  )}
                </div>
              ) : null}

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <LSRgDisplay02
                  style={{
                    color: "#000000",
                    marginBottom: isMobile ? 20 : 31,
                    fontSize: isMobile ? 16 : 24,
                    lineHeight: isMobile ? "22.4px" : "33.6px",
                  }}
                >
                  {
                    categoryList.find((item) => item.value === categoryValue)
                      ?.title
                  }
                </LSRgDisplay02>

                {isMobile ? null : (
                  <MainBtn
                    onClick={() => {
                      let isLogin = sessionStorage.getItem("isLogin");
                      if (isLogin === "true") {
                        navigate(route.community_create);
                      } else {
                        toast("글쓰기를 위해 로그인을 해주세요.");
                        navigate(`${route.login}`, {
                          state: { path: window.location.href },
                        });
                      }
                    }}
                    style={{
                      width: 72,
                      height: 36,
                      borderRadius: 8,
                    }}
                  >
                    <LSRgBody01
                      style={{
                        color: "#FFFFFF",
                        textAlign: "center",
                        marginTop: macOSCheck() ? 0 : 2,
                      }}
                    >
                      글쓰기
                    </LSRgBody01>
                  </MainBtn>
                )}
              </div>
              {isLoading ? (
                <Loading />
              ) : communityList.length > 0 ? (
                communityList.map((item, index) => {
                  return (
                    <CommunityItem
                      key={item.id}
                      data={item}
                      style={{
                        borderBottom:
                          communityList.length === index + 1
                            ? "1px solid #DBDDE0"
                            : "none",
                      }}
                      categoryValue={categoryValue}
                    />
                  );
                })
              ) : (
                <Empty title={"커뮤니티가 없습니다."} />
              )}

              {!isLoading && communityList.length > 0 ? (
                <Pagination
                  route={route.community_list}
                  queryString={`categoryValue=${categoryValue}`}
                  currentPage={Number(page)}
                  totalPages={pagination.totalPages}
                  rangeSize={pagination.rangeSize}
                />
              ) : null}
            </div>
          </div>

          {isMobile ? (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
                width: "100%",
                position: "sticky",
                bottom: 20,
              }}
            >
              <WriteBtnMobile
                onClick={() => {
                  let isLogin = sessionStorage.getItem("isLogin");
                  if (isLogin === "true") {
                    navigate(route.community_create);
                  } else {
                    toast("글쓰기를 위해 로그인을 해주세요.");
                    navigate(`${route.login}`, {
                      state: { path: window.location.href },
                    });
                  }
                }}
              >
                <img
                  style={{
                    width: 30,
                    height: 30,
                    objectFit: "cover",
                  }}
                  src={"/assets/icons/pen_white.png"}
                  alt="글쓰기"
                />
                <LSRgCaption02 style={{ color: "#fff" }}>글쓰기</LSRgCaption02>
              </WriteBtnMobile>
            </div>
          ) : null}
        </PCContainer>
      </CommunityListSection>
    </>
  );
};

export default CommunityList;

const CommunityListSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const TabContainer = styled.div.attrs((props) => {})`
  width: 175px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Tab = styled.button.attrs((props) => {})`
  width: 175px;
  height: 45px;
  background-color: ${(props) => (props.$isActive ? "#5050FF" : "transparent")};
  border: ${(props) => (props.$isActive ? "" : "1px solid #A4A8B1")};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WriteBtnMobile = styled.button.attrs((props) => {})`
  position: sticky;
  bottom: 20px;
  right: 20px;
  width: 75px;
  height: 75px;
  background-color: #5050ff;
  filter: drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.25));
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;
